import { Arena, type CompareResult, type LiveStat } from "./Arena";
import { benchScenes } from "./scenes";
import { engines } from "./engines";

/** Wire the benchmark section: scene picker, live field of engines, measured run. */
export function initBenchmark(): void {
	const arenaEl = document.getElementById("bench-arena");
	const scenesEl = document.getElementById("bench-scenes");
	const runBtn = document.getElementById("bench-run") as HTMLButtonElement | null;
	const resultEl = document.getElementById("bench-result");

	if (!arenaEl || !scenesEl || !runBtn || !resultEl) return;

	// Build one live pane per engine from the registry.
	const msEls = new Map<string, HTMLElement>();
	const lanes = engines.map((def) => {
		const pane = document.createElement("figure");
		pane.className = "bench-pane";
		pane.style.setProperty("--hue", String(def.hue));

		const cap = document.createElement("figcaption");
		const dot = document.createElement("span");
		dot.className = "dot";
		const name = document.createElement("strong");
		name.textContent = def.name;
		const ms = document.createElement("b");
		ms.innerHTML = `<span>–</span> ms`;
		cap.append(dot, name, ms);
		msEls.set(def.id, ms.querySelector("span")!);

		const holder = document.createElement("div");
		holder.className = "bench-canvas-holder";
		const canvas = document.createElement("canvas");
		holder.appendChild(canvas);

		pane.append(cap, holder);
		arenaEl.appendChild(pane);
		return { def, canvas };
	});

	const onLive = (stats: LiveStat[]): void => {
		for (const s of stats) {
			const el = msEls.get(s.id);
			if (el) el.textContent = s.msPerStep > 0 ? s.msPerStep.toFixed(2) : "–";
		}
	};

	const arena = new Arena(lanes, onLive);

	const buttons: HTMLButtonElement[] = [];
	// Index 0 is the aggregate "Overall" view; real scenes follow.
	let overallSelected = true;

	function select(index: number): void {
		buttons.forEach((b, i) => b.classList.toggle("active", i === index));
		resultEl!.hidden = true;
		overallSelected = index === 0;
		// The live arena always animates a concrete scene; for "Overall" we show
		// the box pyramid (the toughest, most familiar scene) while the measured
		// run covers every scene.
		arena.load(overallSelected ? benchScenes[0]! : benchScenes[index - 1]!);
	}

	const overallBtn = document.createElement("button");
	overallBtn.type = "button";
	overallBtn.className = "bench-scene-btn";
	overallBtn.innerHTML = `<b>Overall</b><span>All scenes · balanced verdict</span>`;
	overallBtn.addEventListener("click", () => select(0));
	buttons.push(overallBtn);
	scenesEl.appendChild(overallBtn);

	benchScenes.forEach((scene, i) => {
		const btn = document.createElement("button");
		btn.type = "button";
		btn.className = "bench-scene-btn";
		btn.innerHTML = `<b>${scene.title}</b><span>${scene.blurb}</span>`;
		btn.addEventListener("click", () => select(i + 1));
		buttons.push(btn);
		scenesEl.appendChild(btn);
	});

	runBtn.addEventListener("click", () => void runBenchmark());

	async function runBenchmark(): Promise<void> {
		runBtn!.disabled = true;
		resultEl!.hidden = false;
		try {
			const onPhase = (label: string): void => {
				resultEl!.innerHTML = `<p class="bench-status">${label}</p>`;
			};
			const result = overallSelected
				? await arena.runOverall(benchScenes, onPhase)
				: await arena.runComparison(onPhase);
			renderResult(resultEl!, result);
		} finally {
			runBtn!.disabled = false;
		}
	}

	select(0);

	// The arena animates four physics engines at once — only let it run while the
	// benchmark section is actually on screen so it doesn't throttle the demos.
	new IntersectionObserver(
		(entries) => {
			for (const e of entries) arena.setVisible(e.isIntersecting);
		},
		{ threshold: 0 },
	).observe(arenaEl);
}

function renderResult(host: HTMLElement, r: CompareResult): void {
	const fastest = r.results[0]!;
	const fast2d = r.results.find((x) => x.primary);
	const rank = fast2d ? r.results.indexOf(fast2d) + 1 : 0;

	let headline: string;
	if (fast2d && fast2d.id === fastest.id) {
		const second = r.results[1];
		const factor = second ? fast2d.stepsPerSec / second.stepsPerSec : 1;
		const scope = r.scene.startsWith("Overall") ? "fastest overall" : "fastest";
		headline = `Fast2D is ${scope} — <b>${factor.toFixed(2)}×</b> ahead of the next engine`;
	} else if (fast2d) {
		const factor = fastest.stepsPerSec / fast2d.stepsPerSec;
		headline = `Fast2D placed <b>#${rank}</b> of ${r.results.length} — ${fastest.name} led by <b>${factor.toFixed(2)}×</b>`;
	} else {
		headline = `${fastest.name} was fastest`;
	}

	const rows = r.results
		.map((res, i) => {
			const cls = res.primary ? "row-fast" : "";
			const swatch = `style="color:hsl(${res.hue} 70% 62%)"`;
			const sweeps = `${res.approxSweeps ? "~" : ""}${res.solverSweeps}`;
			return `<tr class="${cls}">
				<td><span class="bench-rank">${i + 1}</span></td>
				<td><span class="bench-swatch" ${swatch}>●</span>${res.name}</td>
				<td>${res.bodies}</td>
				<td>${sweeps}</td>
				<td>${res.msPerStep.toFixed(3)}</td>
				<td>${Math.round(res.stepsPerSec).toLocaleString()}</td>
			</tr>`;
		})
		.join("");

	host.innerHTML = `
		<p class="bench-headline">${headline}</p>
		<table class="bench-table">
			<thead>
				<tr><th>#</th><th>Engine</th><th>Bodies</th><th>Solver sweeps / step</th><th>ms / step</th><th>steps / sec</th></tr>
			</thead>
			<tbody>${rows}</tbody>
		</table>
		<p class="bench-meta">${r.scene} · median of ${r.samples} isolated windows · ${r.steps} measured steps per window · every engine runs its out-of-the-box defaults. Fast2D takes 4 short temporal substeps (one solve each); Matter.js, Planck.js and p2.js take a single step with ~10–11 internal Gauss–Seidel iterations. Fast2D does fewer total solver sweeps and is still faster — so raw ms / step is the honest comparison.</p>
	`;
}
